import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";
import FeedbackWidget from "./FeedbackWidget";
import UserAvatar from "./UserAvatar";

const NAV = [
 { to: "/how-it-works", label: "HOW IT WORKS" },
 { to: "/feed", label: "FEED" },
 { to: "/rappers", label: "RAPPERS" },
 { to: "/cypher", label: "CYPHER" },
 { to: "/studio", label: "STUDIO" },
 { to: "/charts", label: "CHARTS" },
 { to: "/leaderboard", label: "LEADERBOARD" },
 { to: "/battles", label: "BATTLES" },
];

const PAID_TIERS = new Set(["battle_pass", "champion_pass"]);

// Standalone routes — landing pages for products that live inside Young
// Tongues but should FEEL like their own separate site (own domain via
// namecheap URL forward: smartket.help, openpersona.help). We render
// them without the Young Tongues chrome (marquee, header, nag bar,
// mobile bottom-nav, YT footer) so visitors see only that product.
// Prefix match — any nested route under these products counts as
// standalone (e.g. /smartket/board, /smartket/join, /smartket/slip).
const STANDALONE_PREFIXES = ["/smartket", "/openpersona"];

// Porch-light dot in the header: lights up when Neo has left Paul a new note.
function NeoNewsLight() {
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (loc.pathname === "/porch") {
      axios.post(`${API}/site-manager/news/seen`).catch(() => {});
      setUnread(0);
      return;
    }
    const load = () => axios.get(`${API}/site-manager/news`)
      .then(r => { if (!cancelled) setUnread(r.data.unread || 0); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [loc.pathname]);
  return (
    <Link to="/porch" data-testid="neo-news-link" title={unread ? "Neo left you a note" : "The porch"}
          className="inline-flex items-center gap-1.5 font-mono text-xs tracking-widest hover:text-yt-yellow">
      {unread > 0 ? (
        <span data-testid="neo-news-badge" className="bg-[#f2b06b] text-black text-[9px] font-mono px-1.5 py-0.5 rounded-full animate-pulse">
          {unread}
        </span>
      ) : <span className="inline-block w-2 h-2 rounded-full bg-white/30" />}
      <span className="hidden lg:inline">NEO</span>
    </Link>
  );
}

export default function Layout({ children }) {
 const { user, logout } = useAuth();
 const loc = useLocation();
 const nav = useNavigate();
 const isStandalone = STANDALONE_PREFIXES.some(p =>
   loc.pathname === p || loc.pathname.startsWith(p + "/")
 );

 // Pending challenge count for INBOX badge (incoming + outgoing waiting on someone).
 const [pendingCount, setPendingCount] = useState(0);
 useEffect(() => {
   if (!user) { setPendingCount(0); return; }
   let cancelled = false;
   const load = () => axios.get(`${API}/challenges/mine`)
     .then(r => {
       if (cancelled) return;
       const inc = (r.data.incoming || []).filter(c => c.status === "pending" || c.status === "scheduled").length;
       const out = (r.data.outgoing || []).filter(c => c.status === "pending" || c.status === "scheduled").length;
       setPendingCount(inc + out);
     })
     .catch(() => {});
   load();
   const iv = setInterval(load, 30000);
   return () => { cancelled = true; clearInterval(iv); };
 }, [user]);

 // Needs-a-pass = signed-out OR signed-in-but-spectator/free. Admin bypass.
 const needsPass = !user || (user.role !== "admin" && !PAID_TIERS.has(user.tier));
 const showPassBar = needsPass && !loc.pathname.startsWith("/subscribe") && !loc.pathname.startsWith("/login") && !loc.pathname.startsWith("/register");

 // Standalone branch — a totally clean wrapper for SmartKet + OpenPersona.
 // Just the page content and a whisper-quiet attribution footer. No
 // Young Tongues header, marquee, mobile nav, or nag bar. So when a
 // visitor lands from smartket.help they see SmartKet, period.
 if (isStandalone) {
  return (
   <div className="min-h-screen flex flex-col bg-black text-white font-body">
    <main className="flex-1">{children}</main>
    <footer className="border-t border-white/10 py-6 px-6" data-testid="standalone-footer">
     <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-[10px] font-mono tracking-widest text-white/40">
      <div>
       BUILT BY{" "}
       <Link to="/story" data-testid="standalone-founder-link" className="text-white/70 hover:text-yt-yellow transition-colors">
        PAUL DeGAGNÉ
       </Link>
       <span className="mx-2">·</span>
       POWERED BY{" "}
       <Link to="/" data-testid="standalone-yt-link" className="text-white/70 hover:text-yt-yellow transition-colors">
        YOUNG TONGUES
       </Link>
      </div>
      <div>© {new Date().getFullYear()}</div>
     </div>
    </footer>
    <FeedbackWidget />
   </div>
  );
 }

 return (
 <div className="min-h-screen flex flex-col bg-yt-bg text-white font-body">
 {/* Top judge marquee */}
 <div className="bg-black border-b border-yt-border overflow-hidden h-7 flex items-center">
 <div className="marquee-track text-[10px] font-mono tracking-widest text-yt-judge whitespace-nowrap">
 {Array(2).fill(null).map((_, i) => (
 <span key={i} className="px-8">
 ORDER IN THE CYPHER &nbsp;//&nbsp; JUDGE DEGAGNE PRESIDING &nbsp;//&nbsp; SPIT · DROP · CROWN &nbsp;//&nbsp; RAP BATTLES + MONTHLY CHARTS &nbsp;//&nbsp; STEP IN THE ARENA &nbsp;//&nbsp; YOUNG TONGUES //
 </span>
 ))}
 </div>
 </div>

 {/* Header */}
 <header className="border-b border-yt-border bg-yt-bg/95 backdrop-blur sticky top-0 z-30">
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-wrap 2xl:flex-nowrap items-center justify-between gap-x-6 gap-y-4">
 <Link to="/" data-testid="logo-link" className="flex flex-col leading-none shrink-0">
 <span className="font-display text-3xl md:text-4xl tracking-tight whitespace-nowrap">
 <span className="text-yt-yellow">YOUNG</span>{" "}
 <span className="text-yt-red">TONGUES</span>
 </span>
 <span className="text-[9px] font-mono tracking-[0.3em] text-yt-muted mt-1">RIPPEN RAP BATTLES · AI SONG CHARTS</span>
 </Link>

 <nav className="hidden md:flex order-last 2xl:order-none w-full 2xl:w-auto justify-center 2xl:justify-start items-center gap-6 2xl:gap-8 whitespace-nowrap">
 {NAV.map(n => (
 <Link
 key={n.to}
 to={n.soon ? "/" : n.to}
 data-testid={`nav-${n.label.toLowerCase()}`}
 onClick={(e) => { if (n.soon) e.preventDefault(); }}
 className={`font-display text-base tracking-wider transition-colors relative ${
 loc.pathname === n.to ? "text-yt-yellow" : "text-white hover:text-yt-yellow"
 } ${n.soon ? "cursor-not-allowed opacity-70" : ""}`}
 title={n.soon ? "Launching after the contest closes" : undefined}
 >
 {n.label}
 {n.soon && (
 <span className="absolute -top-2 -right-6 bg-yt-red text-white font-mono text-[7px] tracking-widest px-1 py-0.5">SOON</span>
 )}
 </Link>
 ))}
 </nav>

 <div className="flex items-center gap-3 shrink-0">
 {/* LOUD BATTLE PASS CTA — always visible for anyone without a
 paid tier. Bright yellow button, pulses subtly. Impossible
 to miss. Bypassed for admins. */}
 {needsPass && (
 <Link
 to="/subscribe"
 data-testid="header-get-pass"
 className="hidden sm:inline-flex items-center gap-1 bg-yt-yellow text-black font-display text-sm md:text-base tracking-wider px-4 py-2 shadow-brutal hover:bg-white transition-colors animate-pulse"
 >
 GET A PASS
 </Link>
 )}
 {user ? (
 <>
 {user.role === "admin" && (
 <Link to="/admin" data-testid="admin-link" className="hidden md:inline-block px-3 py-2 text-xs font-mono tracking-widest border border-yt-judge text-yt-judge hover:bg-yt-judge hover:text-black transition-colors">
 COMMAND
 </Link>
 )}
{user.role === "admin" && <NeoNewsLight />}
 <Link to="/inbox" data-testid="nav-inbox" className="hidden md:inline-flex relative mr-3 font-mono text-xs tracking-widest hover:text-yt-yellow">
 INBOX
 {pendingCount > 0 && (
 <span data-testid="nav-inbox-badge" className="absolute -top-2 -right-4 bg-yt-red text-white text-[9px] font-mono px-1.5 py-0.5 rounded-full">
 {pendingCount}
 </span>
 )}
 </Link>
 <Link to="/me" data-testid="me-link" className="font-display text-sm tracking-wider hover:text-yt-yellow flex items-center gap-2">
 <UserAvatar portrait={user.portrait} name={user.rapper_name} size="sm" className="!border-yt-yellow" />
 <span className="hidden lg:inline max-w-[9rem] truncate">{user.rapper_name}</span>
 </Link>
 <button
 data-testid="logout-btn"
 onClick={() => { logout(); nav("/"); }}
 className="px-3 py-2 text-xs font-mono tracking-widest border border-yt-border hover:border-yt-red hover:text-yt-red transition-colors"
 >
 LOGOUT
 </button>
 </>
 ) : (
 <>
 <Link to="/login" data-testid="header-login-link" className="font-display text-sm tracking-wider hover:text-yt-yellow">SIGN IN</Link>
 <Link to="/register" data-testid="header-register-link" className="bg-yt-yellow text-black font-display text-sm tracking-wider px-4 py-2 hover:bg-white transition-colors shadow-brutal">
 ENTER ARENA
 </Link>
 </>
 )}
 </div>
 </div>
 </header>

 {/* PASS-NAG BAR — persistent strip under the header for anyone
 browsing without a Battle Pass. Impossible to miss. Auto-hides
 on the subscribe/login/register pages so it doesn't stack. */}
 {showPassBar && (
 <Link
 to="/subscribe"
 data-testid="pass-nag-bar"
 className="block bg-yt-yellow text-black hover:bg-white transition-colors border-b-2 border-black"
 >
 <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
 <div className="font-mono text-[10px] md:text-xs tracking-widest">
 {user ? "NO ACTIVE PASS · SPECTATOR MODE" : "NOT SIGNED IN"}
 <span className="hidden sm:inline"> — get a pass to spit bars & drop tracks. Listening is always free. Tap here.</span>
 </div>
 <div className="font-display text-sm md:text-base tracking-widest bg-black text-yt-yellow px-3 py-1">
 GET PASS ▶
 </div>
 </div>
 </Link>
 )}

 {/* MOBILE BOTTOM NAV — always visible on phones. Three tabs:
 Arena, Inbox (with pending badge), Charts. Hidden on md+ (desktop
 uses top nav). */}
 <div data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black border-t-4 border-yt-yellow grid grid-cols-3">
 <Link to="/battles" data-testid="mobile-nav-arena"
 className={`py-3 text-center font-display text-sm tracking-widest ${loc.pathname.startsWith("/battles") ? "text-yt-yellow bg-yt-yellow/10" : "text-white"}`}>
 ARENA
 </Link>
 <Link to="/inbox" data-testid="mobile-nav-inbox"
 className={`relative py-3 text-center font-display text-sm tracking-widest ${loc.pathname.startsWith("/inbox") ? "text-yt-yellow bg-yt-yellow/10" : "text-white"}`}>
 INBOX
 {user && pendingCount > 0 && (
 <span data-testid="mobile-nav-inbox-badge" className="absolute top-1 right-4 bg-yt-red text-white text-[9px] font-mono px-1.5 py-0.5 rounded-full">
 {pendingCount}
 </span>
 )}
 </Link>
 <Link to="/charts" data-testid="mobile-nav-charts"
 className={`py-3 text-center font-display text-sm tracking-widest ${loc.pathname.startsWith("/charts") ? "text-yt-red bg-yt-red/10" : "text-white"}`}>
 CHARTS
 </Link>
 </div>

 <main className="flex-1 pb-16 md:pb-0">{children}</main>

 <footer className="border-t border-yt-border mt-20 py-10 px-6">
 <div className="max-w-7xl mx-auto flex flex-col gap-4 text-xs font-mono tracking-widest text-yt-muted">
   <div className="flex flex-col md:flex-row items-center justify-between gap-4">
     <div>© {new Date().getFullYear()} YOUNG TONGUES · RIPPEN RAP BATTLES · PATENT PENDING</div>
     <div className="flex gap-4">
       <span>JUDGE: DEGAGNE v1</span>
       <span className="text-yt-judge">● LIVE</span>
     </div>
   </div>
   {/* Build partner credit — Emergent is Paul's co-build partner on this
       platform. Attribution is important to Paul. */}
   <div className="pt-4 border-t border-yt-border/50 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px]">
     <div className="text-white/60">
       BUILT BY <Link to="/about" className="text-yt-yellow hover:underline" data-testid="footer-founder-link">PAUL DeGAGNÉ</Link>
       <span className="mx-2">·</span>
       IN PARTNERSHIP WITH{" "}
       <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="text-yt-yellow hover:underline" data-testid="footer-emergent-link">
         EMERGENT
       </a>
     </div>
     <div className="flex items-center gap-4">
       <Link to="/story" className="text-yt-red hover:text-yt-yellow font-mono text-xs tracking-widest" data-testid="footer-story-link">
         ▶ WATCH THE STORY
       </Link>
       <Link to="/about" className="text-yt-muted hover:text-yt-yellow" data-testid="footer-about-link">
         OUR STORY ▶
       </Link>
     </div>
   </div>
 </div>
 </footer>
 <FeedbackWidget />
 </div>
 );
}
